import { AxiosResponse } from 'axios';
import { getCookie } from 'commercial-web-client-utilities/dist/cookie';
import { NonsecureService } from 'commercial-web-client-utilities/dist/composables/nonsecure-service';
import { LedsService } from 'commercial-web-client-utilities/dist/composables/leds-service';

export interface PasswordHelpServiceOptions {
    nonSecureService: NonsecureService;
    ledsService: LedsService;
}

interface PasswordUpdateOptions {
    updatePasswordToken: string;
    payload: PasswordUpdate;
    signal: AbortSignal;
}

interface PasswordUpdate {
    newPassword: string;
}

interface PasswordUpdateResponse {
    uuid?: string;
    success: boolean;
    message: string;
}
/**
 * Define service APIs consumable by the client
 * @property signal {AbortSignal}
 */
export interface PasswordHelpService {
    updatePassword(options: UpdatePasswordOptions): Promise<AxiosResponse<UpdatePasswordResponse>>;
    getUsername(options: GetUserNameOptions): Promise<AxiosResponse<GetUsernameResponse>>;
    getSecurePasswordResetLink(options: GetSecureResetPasswordLinkOptions):
     Promise<AxiosResponse<GetSecureResetPasswordResponse>>;
    passwordInterrupt(options: PasswordUpdateOptions): Promise<AxiosResponse<PasswordUpdateResponse>>;
    getUsernameForpasswordInterrupt(
        options: PasswordInterruptUserNameRequest): Promise<AxiosResponse<PasswordInterruptUserNameResponse>>;
}

interface UpdatePasswordOptions {
    newPassword: string;
    signal?: AbortSignal;
    token: string;
}

interface UpdatePasswordResponse {
    passwordUpdateStatus: string;
}

interface GetUsernameResponse {
    username: string;
    recoveryToken: string;
}

interface GetSecureResetPasswordLinkOptions {
    username: string;
    signal?: AbortSignal;
    getUsernameToken?: string;
    dob: string;
}

interface GetSecureResetPasswordResponse {
    emailDomain: string;
}

export interface GetUserNameOptions {
    signal?: AbortSignal;
    recoveryIdentifier: string;
}

interface PasswordInterruptUserNameRequest {
    signal?: AbortSignal;
    updatePasswordToken: string;
}
interface PasswordInterruptUserNameResponse {
  username: string;
  groupNumber: string;
  businessSector: string;
  clientNumber: string;
  subClientNumber: string;
}

export function createPasswordHelpService(passwordHelpServiceOptions:
    PasswordHelpServiceOptions): PasswordHelpService {
    const { nonSecureService, ledsService } = passwordHelpServiceOptions;
    const nonSecureServiceClient = nonSecureService.client;
    const ledsClient = ledsService.client;
    const baseUrl = ledsClient.defaults.baseURL;
    const region = getCookie('selectedRegion');

    async function updatePassword(options: UpdatePasswordOptions): Promise<AxiosResponse<UpdatePasswordResponse>> {
        const password = options.newPassword;
        const signal = options.signal;
        const token = options.token;
        const headers = await ledsService.createAuthorizationHeaders();
        if (token) {
            headers.Authorization = token;
        }
        if (region) headers['X-Region'] = region;
        const serviceUrl = `${baseUrl}member/profile-recovery/password`;
        const response = await nonSecureServiceClient.put(serviceUrl, { password }, { headers, signal });
        response.data = response?.data ? response.data : {};
        return response;
    }

    async function getSecurePasswordResetLink(options: GetSecureResetPasswordLinkOptions):
     Promise<AxiosResponse<GetSecureResetPasswordResponse>> {
        const username = options.username;
        const signal = options.signal;
        const payload = {
            withUsername: {
                username,
                dateOfBirth: options.dob ?? ''
            }
        };
        const headers = await ledsService.createAuthorizationHeaders();
        if (region) headers['X-Region'] = region;
        const serviceUrl = `${baseUrl}member/profile-recovery/secureLink`;
        const response = await nonSecureServiceClient.post(serviceUrl, { ...payload }, { headers, signal });
        response.data = response?.data ? response.data : {};
        return response;
    }

    async function getUsername(options: GetUserNameOptions): Promise<AxiosResponse<GetUsernameResponse>> {
        if (!nonSecureServiceClient) throw new Error('Nonsecure client required.');
        const signal = options.signal;
        const headers = await ledsService.createAuthorizationHeaders();
        if (region) headers['X-Region'] = region;
        if (options.recoveryIdentifier) {
            headers.Authorization = options.recoveryIdentifier;
        }
        const url = `${baseUrl}member/profile-recovery/username`;

        return await nonSecureServiceClient.get(url, { headers, signal });
    }

    async function passwordInterrupt(options: PasswordUpdateOptions): Promise<AxiosResponse<PasswordUpdateResponse>> {
        if (!nonSecureServiceClient) throw new Error('Nonsecure client required.');
        const signal = options.signal;
        const headers = await ledsService.createAuthorizationHeaders();
        const payload = options.payload;
        if (region) headers['X-Region'] = region;
        if (options.updatePasswordToken) {
            headers.Authorization = options.updatePasswordToken;
        }
        const serviceUrl = `${baseUrl}member/authenticate/passwordUpdate`;
        const response = await nonSecureServiceClient.post(serviceUrl, { ...payload }, { headers, signal });
        return response;
    }

    async function getUsernameForpasswordInterrupt(
        options: PasswordInterruptUserNameRequest): Promise<AxiosResponse<PasswordInterruptUserNameResponse>> {
        const signal = options.signal;
        const headers = await ledsService.createAuthorizationHeaders();
        if (region) headers['X-Region'] = region;
        if (options.updatePasswordToken) {
            headers.Authorization = options.updatePasswordToken;
        }
        const serviceUrl = `${baseUrl}member/authenticate/redirect/branding`;
        const response = await nonSecureServiceClient.get(serviceUrl, { headers, signal });
        return response;
    }

    return {
        updatePassword,
        getSecurePasswordResetLink,
        getUsername,
        passwordInterrupt,
        getUsernameForpasswordInterrupt
    };
}
